import { EmployeeHomeDashboard } from '../EmployeeHomeDashboard';
import { fixtureEmployeeHomeBalances, fixtureEmployeeHomeWorker } from '../fixtures';
import type {
  EmployeeHomeViewer,
  EmployeeHomeWorkflowSummary,
  HomePageKey,
  HomePageVisibility,
} from '../model';
import type { EmployeeHomePort } from '../port';
import { useAsyncResource } from '../../../shared/useAsyncResource';

export type EmployeeHomePageProps = {
  port: EmployeeHomePort;
  viewer: EmployeeHomeViewer;
  fixtureMode: boolean;
  visiblePages: HomePageVisibility;
  workflowSummary: EmployeeHomeWorkflowSummary;
  onNavigate: (page: HomePageKey, query?: string) => void;
};

export function EmployeeHomePage({
  port,
  viewer,
  fixtureMode,
  visiblePages,
  workflowSummary,
  onNavigate,
}: EmployeeHomePageProps) {
  const worker = useAsyncResource(
    () => fixtureMode
      ? Promise.resolve(fixtureEmployeeHomeWorker(viewer))
      : viewer.workerId
        ? port.loadWorker(viewer.workerReference ?? viewer.workerId)
        : Promise.resolve(undefined),
    [fixtureMode, port, viewer.impersonating, viewer.workerId, viewer.workerReference],
  );
  const canReadBalances = visiblePages.has('my-absence');
  const balances = useAsyncResource(
    () => !canReadBalances ? Promise.resolve([]) : fixtureMode
      ? Promise.resolve([...fixtureEmployeeHomeBalances])
      : viewer.workerId
        ? port.loadBalances(viewer.workerId)
        : Promise.resolve([]),
    [canReadBalances, fixtureMode, port, viewer.workerId],
  );
  const displayName = worker.data?.displayName || (fixtureMode ? '이정연' : '사용자');
  const balance = balances.data?.[0];

  return <EmployeeHomeDashboard
    displayName={displayName}
    viewerKey={`${viewer.tenant}:${viewer.personId}`}
    visiblePages={visiblePages}
    balance={balance ? {
      label: balance.label || '내 휴가',
      asOf: balance.asOf,
      balance: balance.balance,
      accrued: Number(balance.accrued) + Number(balance.carryOver),
      used: balance.used,
    } : undefined}
    balanceLoading={balances.loading}
    balanceError={balances.error?.message}
    onRetryBalance={balances.reload}
    workflowSummary={workflowSummary}
    onNavigate={onNavigate}
  />;
}
